import * as React from "react";
import * as yup from "yup";

import {
  CompanyContactPersonDto,
  CurrencyIDs,
  EmployeeTypeCategoryID,
  ListItemDto,
  PositionType,
} from "Api/Api";
import { FieldError, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  closeCompanyContactPersonModal,
  saveCompanyContactPersonAsync,
} from "State/Companies/CompanyReducer";
import { Resources, useResource } from "Translations/Resources";

import { yupResolver } from "@hookform/resolvers";
import { Typography } from "@material-ui/core";
import { CompanyContactEditCharges } from "Components/Companies/CompanyContacts/CompanyContactEditCharges";
import { CompanyContactHistory } from "Components/Companies/CompanyContacts/CompanyContactHistory";
import { StyledError } from "Components/Companies/CompanyEdit/CompanyEditBasicInfo/CompanyEditBasicInfoStyles";
import { Autocomplete } from "Components/Shared/Inputs/Autocomplete";
import { Checkbox } from "Components/Shared/Inputs/Checkbox";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import { SubMenu } from "Components/Shared/Menu/SubMenu";
import { PrimaryModal } from "Components/Shared/PrimaryModal";
import dayjs from "dayjs";
import {
  getHumanResourcesEmployeeAutocompleteAsync,
  getHumanResourcesPositionsAutocompleteAsync,
} from "State/HumanResources/HumanResourcesReducer";
import { RootStateType } from "State/Store";
import styled from "styled-components";
import { clearDateDay } from "Utils/DateUtils";
import { nameof } from "Utils/ObjectUtils";
import { isNullOrEmpty } from "Utils/StringUtils";
import { ValidationError } from "yup";

const StyledWrapper = styled.div``;

type Props = {
  person: CompanyContactPersonDto;
  isMarketplaceSupplierCompany: boolean;
  onSave: (person: CompanyContactPersonDto) => void;
};

export type CompanyContactEditFormData = Pick<
  CompanyContactPersonDto,
  "email" | "firstName" | "lastName" | "phone"
> & {
  positionID?: string;
  positionName?: string;
  sourceEmployeeID?: string;
  manDayCharge?: number;
  manDayChargeDateFrom?: string | null;
  currencyID?: CurrencyIDs;
};

export const CompanyContactEdit: React.FunctionComponent<Props> = props => {
  const dispatch = useDispatch();
  const { t } = useResource();
  const [isFormTabActive, setIsFormTabActive] = React.useState(true);

  const validationSchema: yup.ObjectSchema<CompanyContactEditFormData> = yup
    .object({
      firstName: yup
        .string()
        .required(t(Resources.CompanyEdit.ContactEdit.FirstName.Validation))
        .defined(),
      lastName: yup
        .string()
        .required(t(Resources.CompanyEdit.ContactEdit.LastName.Validation))
        .defined(),
      email: yup
        .string()
        .defined()
        .required(t(Resources.CompanyEdit.ContactEdit.Email.Validation))
        .email(t(Resources.CompanyEdit.ContactEdit.Email.WrongFormat)),
      phone: yup
        .string()
        .optional()
        .matches(/^[+]*[0-9]{3} [0-9]{3} [0-9]{3} [0-9]{3}/g, {
          message: t(Resources.CompanyEdit.ContactEdit.Phone.WrongFormat),
          excludeEmptyString: true,
        }),
      sourceEmployeeID: yup.string().test({
        name: "sourceEmployeeID",
        exclusive: false,
        message: "Vyplňte existujícího spolupracovníka",
        test: value => {
          return !existingEmployee || isSourceEmployeeFilled(value);
        },
      }),
      manDayCharge: yup.number().optional(),
      manDayChargeDateFrom: yup
        .string()
        .nullable()
        .test("manDayChargeDateFrom", "NEEE", val => {
          const getMinNewChargeDate = () => {
            const lastCharge = props.person.charges.find(x => !x.dateTo);
            return lastCharge ? new Date(lastCharge.dateFrom) : undefined;
          };
          const minNewChargeDate = getMinNewChargeDate();

          return !minNewChargeDate || new Date(val) >= minNewChargeDate;
        }),
      positionID: !props.isMarketplaceSupplierCompany
        ? yup.string().optional()
        : yup
            .string()
            .notOneOf(
              ["0"],
              t(Resources.CompanyEdit.ContactEdit.Position.Validation),
            )
            .required(t(Resources.CompanyEdit.ContactEdit.Position.Validation)),
      positionName: !props.isMarketplaceSupplierCompany
        ? yup.string().optional()
        : yup.string().defined(),
    })
    .defined();

  const getDefaultValues = React.useCallback(() => {
    const lastCharge = props.person.charges.find(x => !x.dateTo);
    return {
      firstName: props.person.firstName ?? "",
      lastName: props.person.lastName ?? "",
      phone: props.person.phone ?? "",
      manDayCharge: lastCharge?.manDayCharge ?? undefined,
      manDayChargeDateFrom: lastCharge?.dateFrom ?? null,
      currencyID: lastCharge?.currencyID ?? CurrencyIDs.CZK,
      email: props.person.email ?? "",
      positionID: props.person.positionID.toString() ?? null,
      positionName: props.person.positionName ?? null,
      sourceEmployeeID: props.person.sourceEmployeeID?.toString() ?? undefined,
    };
  }, [props]);

  const form = useForm<CompanyContactEditFormData>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: getDefaultValues(),
    reValidateMode: "onSubmit",
  });

  const { register, errors, watch, setValue, setError } = form;
  const formValues = watch();

  const isSourceEmployeeFilled = (sourceEmployeeID: string | undefined) => {
    return sourceEmployeeID !== undefined && !isNullOrEmpty(sourceEmployeeID);
  };

  const isDisabled = () => {
    return (
      isSourceEmployeeFilled(formValues.sourceEmployeeID) || existingEmployee
    );
  };

  const [position, setPosition] = React.useState<ListItemDto>({
    itemID: props.person.positionID,
    name: props.person.positionName,
  });

  const [existingEmployee, setExistingEmployee] = React.useState<boolean>(
    props.person.companyContactPersonID === 0
      ? false
      : isSourceEmployeeFilled(formValues.sourceEmployeeID),
  );

  const [sourceEmployee, setSourceEmployee] = React.useState<ListItemDto>({
    itemID: props.person.sourceEmployeeID ?? 0,
    name: props.person.sourceEmployeeID
      ? `${props.person.firstName} ${props.person.lastName}`
      : "",
  });

  const getName = (dto: CompanyContactPersonDto) => {
    return `${dto.firstName} ${dto.lastName}`;
  };

  const {
    positionsAutocompleteList,
    isPositionsAutocompleteLoading,
    isEmployeesAutocompleteLoading,
    employeesAutocompleteList,
  } = useSelector((e: RootStateType) => e.humanResources);

  const getStringInput = (
    name: keyof CompanyContactEditFormData,
    label: string,
  ) => (
    <div className={name}>
      <TextInput
        label={label}
        name={name}
        disabled={isDisabled()}
        inputRef={register}
        fieldError={errors[name] as FieldError}
      />
    </div>
  );

  React.useEffect(() => {
    register({ name: "firstName" });
    register({ name: "lastName" });
    register({ name: "email" });
    register({ name: "phone" });
    register({ name: "manDayCharge" });
    register({ name: "manDayChargeDateFrom" });
    register({ name: "currencyID" });
    register({ name: "positionID" });
    register({ name: "sourceEmployeeID" });
  }, [register]);

  const fillEmployeeData = (val: ListItemDto | null) => {
    if (employeesAutocompleteList) {
      let item = employeesAutocompleteList.find(
        e => e.employeeID === val?.itemID,
      );
      if (item !== undefined) {
        setValue("firstName", item.firstName, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue("lastName", item.lastName, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue("email", item.email, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue("phone", item.phone, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue("positionID", item.position.positionID?.toString());
        setValue("positionName", item.position.positionName?.toString());
        setPosition({
          itemID: item.position.positionID,
          name: item.position.positionName,
        });
      }
    }
  };

  const handleSubmit = () => {
    const saveObj: CompanyContactPersonDto = {
      ...props.person,
      ...formValues,
      positionID: (formValues.positionID as unknown) as number,
      positionName: position.name,
      sourceEmployeeID: (formValues.sourceEmployeeID as unknown) as number,
    };

    validationSchema
      .validate(saveObj, { abortEarly: false })
      .then(e => {
        if (formValues.manDayCharge && formValues.manDayChargeDateFrom) {
          const lastCharge = saveObj.charges.find(x => !x.dateTo);
          const dateFrom = clearDateDay(
            formValues.manDayChargeDateFrom,
            6,
          ).toISOString();

          if (
            formValues.manDayChargeDateFrom &&
            (!lastCharge ||
              clearDateDay(lastCharge.dateFrom ?? new Date()).toISOString() !==
                clearDateDay(formValues.manDayChargeDateFrom).toISOString())
          ) {
            saveObj.charges.push({
              companyContactPersonChargeID: 0,
              companyContactPersonID: props.person.companyContactPersonID,
              createdBy: 0,
              dateCreated: new Date().toISOString(),
              dateFrom: dateFrom,
              manDayCharge: formValues.manDayCharge,
              currencyID: formValues.currencyID ?? CurrencyIDs.CZK,
              createdByFirstName: "",
              createdByLastName: "",
            });

            if (lastCharge) {
              lastCharge.dateTo = clearDateDay(
                dayjs(dateFrom).subtract(1, "day").toISOString(),
                17,
              ).toISOString();
            }
          } else if (
            lastCharge &&
            (lastCharge.manDayCharge !== formValues.manDayCharge ||
              lastCharge.currencyID !== formValues.currencyID)
          ) {
            lastCharge.manDayCharge = formValues.manDayCharge;
            lastCharge.currencyID = formValues.currencyID ?? CurrencyIDs.CZK;
          }
        }

        dispatch(saveCompanyContactPersonAsync.request(saveObj));
      })
      .catch((err: ValidationError) => {
        err.inner.forEach(e => {
          setError(e.path as any, {
            message: e.errors[0],
            type: "manual",
          });
        });
      });
  };

  return (
    <>
      <PrimaryModal
        title={
          props.person && props.person.companyContactPersonID
            ? getName(props.person)
            : t(Resources.CompanyEdit.ContactEdit.NewContact)
        }
        successBtnText={isFormTabActive ? t(Resources.Common.Save) : undefined}
        closeBtnText={t(Resources.Common.Cancel)}
        onClose={() => {
          dispatch(closeCompanyContactPersonModal());
        }}
        onSuccess={handleSubmit}
        open={props.person !== undefined}
      >
        <SubMenu
          menuItems={[
            {
              name: "Detail",
              onClick: () => setIsFormTabActive(true),
              isActive: isFormTabActive,
            },
            {
              name: "Historie",
              onClick: () => setIsFormTabActive(false),
              isActive: !isFormTabActive,
            },
          ]}
          isHorizontal
        />

        {isFormTabActive ? (
          <form>
            <Checkbox
              label="Existující kontakt"
              checked={existingEmployee}
              name="existing-employee"
              id={"contact-existing"}
              onClick={() => {
                let newValue = !existingEmployee;
                setExistingEmployee(newValue);

                if (!newValue) {
                  setValue("sourceEmployeeID", undefined);
                  setSourceEmployee({ itemID: 0, name: "" });
                }
              }}
            ></Checkbox>

            {existingEmployee && (
              <>
                <Autocomplete
                  value={sourceEmployee}
                  fieldError={errors["sourceEmployeeID"] as FieldError}
                  onSearch={text => {
                    dispatch(
                      getHumanResourcesEmployeeAutocompleteAsync.request({
                        search: text,
                        employeeTypeCategoryIDs: [
                          EmployeeTypeCategoryID.Internal,
                          EmployeeTypeCategoryID.External,
                        ],
                      }),
                    );
                  }}
                  isLoading={isEmployeesAutocompleteLoading}
                  options={(employeesAutocompleteList ?? []).map(e => {
                    return {
                      itemID: e.employeeID,
                      name: e.name,
                    } as ListItemDto;
                  })}
                  onChange={val => {
                    setSourceEmployee(val ?? { itemID: 0, name: "" });
                    setValue("sourceEmployeeID", val?.itemID.toString(), {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    fillEmployeeData(val);
                  }}
                  label="Spolupracovník"
                />

                {errors.sourceEmployeeID && (
                  <StyledError className={"error-sourceEmployeeID"}>
                    <Typography>{errors.sourceEmployeeID?.message}</Typography>
                  </StyledError>
                )}
              </>
            )}

            <StyledWrapper>
              {getStringInput(
                "firstName",
                t(Resources.CompanyEdit.ContactEdit.FirstName.Label),
              )}
              {errors.firstName && (
                <StyledError className={"error-firstName"}>
                  <Typography>{errors.firstName?.message}</Typography>
                </StyledError>
              )}

              {getStringInput(
                "lastName",
                t(Resources.CompanyEdit.ContactEdit.LastName.Label),
              )}
              {errors.lastName && (
                <StyledError className={"error-lastName"}>
                  <Typography>{errors.lastName?.message}</Typography>
                </StyledError>
              )}

              {getStringInput(
                "email",
                t(Resources.CompanyEdit.ContactEdit.Email.Label),
              )}
              {errors.email && (
                <StyledError className={"error-email"}>
                  <Typography>{errors.email?.message}</Typography>
                </StyledError>
              )}

              <div className="phone">
                <TextInput
                  label={t(Resources.CompanyEdit.ContactEdit.Phone.Label)}
                  value={formValues.phone?.split(" ").join("")}
                  name={nameof<CompanyContactEditFormData>("phone")}
                  fieldError={errors.phone}
                  disabled={isDisabled()}
                  mask={"+000 000 000 000"}
                  unmask={"typed"}
                  type={"tel"}
                  onMaskInputChange={value => {
                    setValue(
                      nameof<CompanyContactEditFormData>("phone"),
                      value,
                      {
                        shouldValidate: true,
                      },
                    );
                  }}
                />
              </div>
              {errors.phone && (
                <StyledError className={"error-phone"}>
                  <Typography>{errors.phone?.message}</Typography>
                </StyledError>
              )}

              {props.isMarketplaceSupplierCompany && (
                <>
                  <CompanyContactEditCharges
                    form={form}
                    isDisabled={isDisabled}
                    person={props.person}
                  ></CompanyContactEditCharges>

                  {existingEmployee && (
                    <>
                      <TextInput
                        value={position.name}
                        disabled={true}
                        label={t(
                          Resources.CompanyEdit.ContactEdit.Position.Label,
                        )}
                        name="positionID"
                      />
                    </>
                  )}
                  {!existingEmployee && (
                    <>
                      <Autocomplete
                        value={position}
                        onSearch={text => {
                          dispatch(
                            getHumanResourcesPositionsAutocompleteAsync.request(
                              {
                                search: text,
                                positionType: existingEmployee
                                  ? PositionType.Internal
                                  : PositionType.External,
                              },
                            ),
                          );
                        }}
                        isLoading={isPositionsAutocompleteLoading}
                        options={positionsAutocompleteList ?? []}
                        onChange={val => {
                          setValue(
                            nameof<CompanyContactEditFormData>("positionID"),
                            val?.itemID.toString(),
                            { shouldValidate: true },
                          );
                          setPosition(val ?? { itemID: 0, name: "" });
                        }}
                        label={t(
                          Resources.CompanyEdit.ContactEdit.Position.Label,
                        )}
                      />
                      {errors.positionID && (
                        <StyledError className={"error-positionID"}>
                          <Typography>{errors.positionID?.message}</Typography>
                        </StyledError>
                      )}
                    </>
                  )}
                </>
              )}
            </StyledWrapper>
            <br></br>
          </form>
        ) : (
          <CompanyContactHistory
            charges={props.person.charges}
          ></CompanyContactHistory>
        )}
      </PrimaryModal>
    </>
  );
};
